/**
 * Pending-work classifier (FORGE-087 / pending-work-visibility-plan).
 *
 * Pure function over raw DDB-shaped META + STORY rows: derives "what is this
 * epic/story doing and what does it need" — including everything that happens
 * BETWEEN agent sessions (launch failures, startup crashes, operator actions,
 * approval gates, plain waiting). Consumed by:
 *   - GET /api/v1/activity/pending  (Today's "Waiting & needs attention")
 *   - the epic detail banner + story chips
 *
 * Inputs are deliberately loose (Record<string, unknown>) — rows come straight
 * from DynamoDB scans and carry snake_case fields written by Forge:
 *   META : state, updated_at, escalated_to_human, pending_operator_action,
 *          last_launch_error, title, kind, github_issue_url
 *   STORY: state, updated_at, last_launch_error, last_run_crash,
 *          agent_started_at, needs_human, stall_reason, running_agent
 */

export type WorkStatusKind =
  | 'running'
  | 'awaiting_approval'
  | 'queued'
  | 'launch_failed'
  | 'crashed_at_startup'
  | 'operator_action_required'
  | 'stalled'
  | 'blocked'
  | 'escalated';

export interface PendingWorkItem {
  /** Stable composite id for React keys: `<pk>|<storyId|epic>|<status>`. */
  id: string;
  scope: 'epic' | 'story';
  repo: string;
  epicNumber: number;
  epicRef: string;
  kind: 'epic' | 'bugfix';
  title: string;
  storyId: string | null;
  state: string;
  status: WorkStatusKind;
  minutesInState: number;
  /** Human-readable explanation (error message / operator action / stall reason). */
  reason: string | null;
  /** Copy-pasteable operator runbook when one exists. */
  runbook: string | null;
  updatedAt: string;
}

/** Epic META states with nothing left to show. Mirrors analytics.ts. */
export const TERMINAL_EPIC_STATES = new Set([
  'READY_FOR_REVIEW', 'READY_FOR_REVIEW_NOTIFIED', 'COMPLETED', 'ABORTED',
  'BUGFIX_COMPLETE',
]);

/** Epic states that wait on a human /proceed-style gate (not a problem). */
// M3 (2026-07-14): added the FORGE-313 requirements-gate + *_IN_PROGRESS
// question states. An epic parked on the requirements gate (or a question
// re-opened) is a confirmed human-wait that sets no pending_operator_action, so
// it was vanishing from "Waiting & needs attention". Kept in sync with the
// cross-repo EPIC_STATES human-wait set (see notes on M3).
const APPROVAL_STATES = new Set([
  'PLAN_POSTED', 'TARGETS_PENDING',
  'PLAN_QUESTIONS', 'PLAN_QUESTIONS_IN_PROGRESS',
  'REQUIREMENTS_QUESTIONS', 'REQUIREMENTS_QUESTIONS_IN_PROGRESS',
]);

/** Story states that are done / intentionally inert. */
const TERMINAL_STORY_STATES = new Set(['TESTED', 'COMPLETE', 'COMPLETED', 'SKIPPED', 'ABORTED', 'DONE']);

/**
 * FORGE-126: a REAL story row is single-repo `STORY#<idx>` or cross-repo
 * `STORY#<org>/<repo>#<idx>`. Synthetic scaffolding pseudo-rows — `STORY#plan`
 * (epic-planner), `STORY#xgraph` (graph-builder), `STORY#smoke-<env>-run`,
 * `STORY#qa-<env>-run` — sit at IN_PROGRESS for the life of the epic but drive
 * nothing. They must NEVER be classified as stalled stories (they were showing
 * up as phantom "needs attention" items). Pass the full SK (with `STORY#`).
 */
export function isRealStorySK(sk: string): boolean {
  return /^STORY#(\d+|[^#]+\/[^#]+#\d+)$/.test(sk);
}

/** Past this many minutes without a row update, waiting becomes "stalled". */
export const STALL_THRESHOLD_MIN = 90;
/** An IN_PROGRESS story older than this has no live agent behind it. */
const RUNNING_FRESHNESS_MIN = 90;

export const SEVERITY: Record<WorkStatusKind, number> = {
  operator_action_required: 0,
  escalated: 1,
  crashed_at_startup: 2,
  launch_failed: 3,
  blocked: 4,
  stalled: 5,
  awaiting_approval: 6,
  queued: 7,
  running: 8,
};

function minutesSince(iso: unknown, nowMs: number): number {
  if (typeof iso !== 'string' || !iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.round((nowMs - t) / 60_000));
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

interface LaunchError { at?: string; agent?: string; kind?: string; message?: string; story_id?: string }
interface RunCrash { at?: string; phase?: string; agent?: string; message?: string }
interface OperatorAction { kind?: string; message?: string; runbook?: string; since?: string; story_id?: string }

export interface ClassifyArgs {
  /** Epic META row (raw). */
  meta: Record<string, unknown>;
  /** STORY# rows for the epic (raw). */
  stories: Record<string, unknown>[];
  /** "org/repo" parsed from the PK by the caller. */
  repo: string;
  epicNumber: number;
  /** Opaque ref the portal uses for /epics/:id links. */
  epicRef: string;
  nowMs?: number;
}

/**
 * Classify one epic + its stories into zero or more PendingWorkItems.
 * Returns [] for terminal epics. Running stories produce no item (they're
 * already visible as sessions); everything else explains itself.
 */
export function classifyEpicWork(args: ClassifyArgs): PendingWorkItem[] {
  const { meta, stories, repo, epicNumber, epicRef } = args;
  const nowMs = args.nowMs ?? Date.now();
  const epicState = str(meta.state);
  if (!epicState || TERMINAL_EPIC_STATES.has(epicState)) return [];

  const kind: 'epic' | 'bugfix' = meta.kind === 'bugfix' ? 'bugfix' : 'epic';
  const title = str(meta.title) || `#${epicNumber}`;
  const items: PendingWorkItem[] = [];

  const base = (over: Partial<PendingWorkItem>): PendingWorkItem => ({
    id: '', scope: 'epic', repo, epicNumber, epicRef, kind, title,
    storyId: null, state: epicState, status: 'queued',
    minutesInState: minutesSince(meta.updated_at, nowMs),
    reason: null, runbook: null, updatedAt: str(meta.updated_at),
    ...over,
  });

  // ── Epic-level signals ─────────────────────────────────────────────────────
  const poa = meta.pending_operator_action as OperatorAction | undefined;
  if (poa && (poa.message || poa.kind)) {
    items.push(base({
      id: `${epicRef}|epic|operator`,
      status: 'operator_action_required',
      reason: poa.message ?? poa.kind ?? null,
      runbook: poa.runbook || null,
      minutesInState: minutesSince(poa.since ?? meta.updated_at, nowMs),
      storyId: poa.story_id ?? null,
    }));
  }
  if (meta.escalated_to_human === true) {
    items.push(base({
      id: `${epicRef}|epic|escalated`,
      status: 'escalated',
      reason: str(meta.stall_reason) || 'Escalated to human review',
    }));
  }
  if (APPROVAL_STATES.has(epicState)) {
    items.push(base({
      id: `${epicRef}|epic|approval`,
      status: 'awaiting_approval',
      reason: epicState === 'PLAN_POSTED'
        ? 'Plan posted — waiting for /proceed on the epic issue'
        : 'Waiting for operator input on the epic issue',
    }));
  }
  if (epicState === 'BLOCKED') {
    items.push(base({ id: `${epicRef}|epic|blocked`, status: 'blocked', reason: 'Epic is BLOCKED — see the epic issue' }));
  }
  const metaLaunchErr = meta.last_launch_error as LaunchError | undefined;
  if (metaLaunchErr?.message && !poa) {
    items.push(base({
      id: `${epicRef}|epic|launch`,
      status: 'launch_failed',
      reason: `${metaLaunchErr.agent ?? 'agent'} launch failed: ${metaLaunchErr.message}`,
      minutesInState: minutesSince(metaLaunchErr.at ?? meta.updated_at, nowMs),
      storyId: metaLaunchErr.story_id ?? null,
    }));
  }

  // ── Story-level signals ────────────────────────────────────────────────────
  // FORGE-133: in a cross-repo epic the canonical story rows are compound
  // (STORY#<org>/<repo>#<idx>). A bare STORY#<n> row in the same partition is an
  // agent-written ghost (the breadcrumb/retry-analyst legacy bug) — skip it so
  // each story isn't counted twice (once compound BLOCKED, once bare IN_PROGRESS).
  const isCrossRepo = meta.model === 'cross' || typeof meta.anchor_repo === 'string';
  for (const story of stories) {
    const sk = str(story.SK);
    // FORGE-126: skip synthetic scaffolding pseudo-rows (STORY#plan, STORY#xgraph,
    // smoke/qa runs). They're permanently IN_PROGRESS and were surfacing as
    // phantom "stalled story" attention items.
    if (!isRealStorySK(sk)) continue;
    if (isCrossRepo && /^STORY#\d+$/.test(sk)) continue; // FORGE-133: bare ghost row
    const storyId = sk.replace(/^STORY#/, '');
    const sState = str(story.state);
    if (!sState || TERMINAL_STORY_STATES.has(sState)) continue;

    const mins = minutesSince(story.updated_at, nowMs);
    const sBase = (over: Partial<PendingWorkItem>): PendingWorkItem => ({
      ...base({ scope: 'story', storyId, state: sState, minutesInState: mins, updatedAt: str(story.updated_at) }),
      ...over,
    });

    const startedAt = str(story.agent_started_at);
    // Breadcrumbs OLDER than the latest successful start are stale — the agent
    // recovered and relaunched since. Only fresh ones drive classification.
    const isFresh = (at?: string) => !startedAt || !at || at >= startedAt;
    const rawCrash = story.last_run_crash as RunCrash | undefined;
    const crash = rawCrash?.message && isFresh(str(rawCrash.at)) ? rawCrash : undefined;
    const rawLaunchErr = story.last_launch_error as LaunchError | undefined;
    const launchErr = rawLaunchErr?.message && isFresh(str(rawLaunchErr.at)) ? rawLaunchErr : undefined;

    // A live agent: IN_PROGRESS and fresh. Already visible as a session — skip.
    if (sState === 'IN_PROGRESS' && mins <= RUNNING_FRESHNESS_MIN && !crash && !launchErr) {
      continue;
    }

    if (sState === 'PAUSED_NEEDS_HUMAN' || story.needs_human === true) {
      items.push(sBase({
        id: `${epicRef}|${storyId}|escalated`,
        status: 'escalated',
        reason: str(story.stall_reason) || 'Paused — needs human review',
      }));
      continue;
    }
    if (sState === 'BLOCKED') {
      items.push(sBase({ id: `${epicRef}|${storyId}|blocked`, status: 'blocked', reason: 'Story is BLOCKED — /unblock after fixing the cause' }));
      continue;
    }
    // Crash breadcrumb newer than the last successful start = the most recent
    // attempt died. boot/setup-phase deaths are the "invisible" class.
    if (crash?.message) {
      const startup = crash.phase === 'boot' || crash.phase === 'setup';
      items.push(sBase({
        id: `${epicRef}|${storyId}|crash`,
        status: startup ? 'crashed_at_startup' : 'stalled',
        reason: `${crash.agent ?? 'agent'} crashed${crash.phase ? ` during ${crash.phase}` : ''}: ${crash.message}`,
        minutesInState: minutesSince(crash.at ?? story.updated_at, nowMs),
      }));
      continue;
    }
    if (launchErr?.message) {
      items.push(sBase({
        id: `${epicRef}|${storyId}|launch`,
        status: 'launch_failed',
        reason: `${launchErr.agent ?? 'agent'} launch failed (${launchErr.kind ?? 'unknown'}): ${launchErr.message}`,
        minutesInState: minutesSince(launchErr.at ?? story.updated_at, nowMs),
      }));
      continue;
    }
    items.push(sBase({
      id: `${epicRef}|${storyId}|wait`,
      status: mins > STALL_THRESHOLD_MIN ? 'stalled' : 'queued',
      reason: mins > STALL_THRESHOLD_MIN
        ? `No progress for ${mins} min in ${sState}`
        : null,
    }));
  }

  items.sort((a, b) => SEVERITY[a.status] - SEVERITY[b.status] || b.minutesInState - a.minutesInState);
  return items;
}

/** Sort a multi-epic list worst-first (severity, then time stuck). */
export function sortPendingWork(items: PendingWorkItem[]): PendingWorkItem[] {
  return [...items].sort((a, b) => SEVERITY[a.status] - SEVERITY[b.status] || b.minutesInState - a.minutesInState);
}
